import { useState, useEffect } from 'react';
import {
  getTypeToSearchTimeout,
  hasAnyFilterSelected,
  shouldUncheckExcludeCompletedForStatus,
  resolveStatusDataLabel,
  getFilterMinLengthError,
  hasCabieLoadFilterMinLengthErrors,
  hasEpoLoadFilterMinLengthErrors,
  resolveToDateForFrom,
  resolveFromDateForTo,
} from './utils';
import { useLoadFilterOptions } from './queryHooks/useLoadFilterOptions';
import FilterField from './FilterField';
import PropTypes from 'prop-types';
import './LoadFiltersV2.css';

const styles = {
  dateGroup: 'load-filters-v2__date-group',
  dateRange: 'load-filters-v2__date-range',
  checkbox: 'load-filters-v2__checkbox',
};

const displayDate = (disabled, value, fallback) => (disabled ? (value ?? '') : value || fallback);

const LoadFiltersV2 = ({ visible, values, defaultValues, onChange, onSubmit, isEPO, isEORIUser, isTraderUser }) => {
  // --------------------
  // 1. Declaring state
  // --------------------
  const { dropdownOptions: baseDropdownOptions } = useLoadFilterOptions({
    isEPO,
    isEORIUser,
    enabled: true,
  });
  const [searchOverrides, setSearchOverrides] = useState({ customerNames: [], customerEoris: [] });
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const dropdownOptions = {
    ...baseDropdownOptions,
    customerNames: searchOverrides.customerNames.length
      ? searchOverrides.customerNames
      : baseDropdownOptions.customerNames,
    customerEoris: searchOverrides.customerEoris.length
      ? searchOverrides.customerEoris
      : baseDropdownOptions.customerEoris,
  };

  const [disabledFields, setDisabledFields] = useState({
    createdDate: false,
    loadDate: false,
  });

  const formHasError = isEPO ? hasEpoLoadFilterMinLengthErrors(values) : hasCabieLoadFilterMinLengthErrors(values);

  // --------------------
  // 2. Hooks & Effects
  // --------------------
  useEffect(() => () => clearTimeout(searchTimeout), [searchTimeout]);

  useEffect(() => {
    // if user has selected a load date, then we need to disable and reset the created date fields
    if (values.loadDateFrom || values.loadDateTo) {
      setDisabledFields((prev) => ({ ...prev, loadDate: false, createdDate: true }));
      handleChange({ target: { name: 'createdDateFrom', value: '' } });
      handleChange({ target: { name: 'createdDateTo', value: '' } });
    } else {
      setDisabledFields((prev) => ({ ...prev, createdDate: false }));
    }
  }, [values.loadDateFrom, values.loadDateTo]);

  useEffect(() => {
    // if user has selected a created date, then we need to disable and reset the load date fields
    if (values.createdDateFrom || values.createdDateTo) {
      setDisabledFields((prev) => ({ ...prev, loadDate: true, createdDate: false }));
      handleChange({ target: { name: 'loadDateFrom', value: '' } });
      handleChange({ target: { name: 'loadDateTo', value: '' } });
    } else {
      setDisabledFields((prev) => ({ ...prev, loadDate: false }));
    }
  }, [values.createdDateFrom, values.createdDateTo]);

  // -------------------------------
  // 3. Change functions for form
  // -------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange((prev) => {
      const next = { ...(prev ?? {}), [name]: value };
      if (name === 'Status' || name === 'epoStatus') {
        const label = resolveStatusDataLabel(value, dropdownOptions.statuses);
        next.excludeCompleted = shouldUncheckExcludeCompletedForStatus(label) ? 0 : 1;
      }
      return next;
    });
  };

  const handleDateFromChange = (fromField, toField, toDefault) => (e) => {
    const from = e.target.value || '';
    onChange((prev) => {
      const base = prev ?? {};
      if (!from) {
        return { ...base, [fromField]: '', [toField]: '' };
      }
      const effectiveTo = base[toField] ?? toDefault ?? '';
      return {
        ...base,
        [fromField]: from,
        [toField]: resolveToDateForFrom(from, effectiveTo),
      };
    });
  };

  const handleDateToChange = (fromField, toField, fromDefault) => (e) => {
    const to = e.target.value || '';
    onChange((prev) => {
      const base = prev ?? {};
      if (!to) {
        return { ...base, [fromField]: '', [toField]: '' };
      }
      const from = base[fromField] || fromDefault || '';
      return {
        ...base,
        [toField]: to,
        [fromField]: resolveFromDateForTo(to, from),
      };
    });
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    onChange((prev) => ({ ...prev, [e.target.name]: isChecked ? 1 : 0 }));
  };

  const handleSearchChange = (searchTerm, field) => {
    clearTimeout(searchTimeout);

    if (!searchTerm || searchTerm.trim().length === 0) {
      setIsSearchLoading(false);
      setSearchOverrides((prev) => ({
        ...prev,
        ...(field === 'name' && { customerNames: [] }),
        ...(field === 'eori' && { customerEoris: [] }),
      }));
      return;
    }

    setIsSearchLoading(true);
    const newTimeout = getTypeToSearchTimeout(searchTerm, field, setSearchOverrides, setIsSearchLoading);
    setSearchTimeout(newTimeout);
  };

  if (!visible) return null;

  const showTraderField = !(isEPO && isTraderUser);
  const showEpoCreatedBy = isEPO && isEORIUser;
  const showEpoType = isEPO && isEORIUser;
  const showCabieExtraFields = !isEPO;

  // --------------------
  // 4. Main component
  // --------------------
  return (
    <div className="px-3 mt-4 load-filters-v2">
      <div className="load-filters-v2__grid">
        <div className="load-filters-v2__cell">
          <FilterField
            label="CABIE Ref"
            field="refNo"
            value={values.refNo}
            onChange={handleChange}
            allowsWildcard
            dataTestId="cabieRef-textField"
          />
        </div>
        <div className="load-filters-v2__cell">
          <FilterField
            label={isEPO ? 'Trader Ref' : 'Job Ref'}
            field="jobReference"
            value={values.jobReference}
            onChange={handleChange}
            allowsWildcard
            dataTestId="traderRef-textField"
          />
        </div>
        <div className="load-filters-v2__cell">
          <FilterField
            label="Route"
            field={isEPO ? 'route' : 'cabieRoute'}
            value={isEPO ? values.route : values.cabieRoute}
            options={dropdownOptions.routes}
            onChange={handleChange}
            dataTestId="route-combobox"
          />
        </div>
        <div className="load-filters-v2__cell">
          <div className={styles.dateGroup}>
            <label className="input-label label-text">
              <span>{isEPO ? 'Arrival / Exit Date' : 'Loading Date'}</span>
            </label>
            <div className={styles.dateRange}>
              <FilterField
                type="date"
                field="loadDateFrom"
                value={displayDate(disabledFields.loadDate, values.loadDateFrom, defaultValues.defaultLoadFrom)}
                onChange={handleDateFromChange('loadDateFrom', 'loadDateTo', defaultValues.defaultLoadTo)}
                disabled={disabledFields.loadDate}
                hideLabel
                dataTestId="arrivalDateFrom-date"
              />
              <FilterField
                type="date"
                field="loadDateTo"
                value={displayDate(disabledFields.loadDate, values.loadDateTo, defaultValues.defaultLoadTo)}
                onChange={handleDateToChange('loadDateFrom', 'loadDateTo', defaultValues.defaultLoadFrom)}
                disabled={disabledFields.loadDate}
                hideLabel
                dataTestId="arrivalDateTo-date"
              />
            </div>
          </div>
        </div>

        <div className="load-filters-v2__cell">
          {showTraderField && (
            <FilterField
              label={isEPO ? 'Trader' : 'Customer'}
              field="customerName"
              value={values.customerName}
              onChange={handleChange}
              error={getFilterMinLengthError('customerName', values.customerName)}
              dataTestId={isEPO ? 'trader-textField' : 'customer-textField'}
            />
          )}
        </div>
        <div className="load-filters-v2__cell">
          {showEpoCreatedBy && (
            <FilterField
              label="Created By"
              field="originator"
              value={values.originator}
              options={dropdownOptions.siteNames}
              onChange={handleChange}
              dataTestId="createdBy-combobox"
            />
          )}
          {showCabieExtraFields && (
            <FilterField
              label="Invoice Ref"
              field="invReference"
              value={values.invReference}
              onChange={handleChange}
              allowsWildcard
            />
          )}
        </div>
        <div className="load-filters-v2__cell">
          <FilterField
            label="Status"
            field={isEPO ? 'epoStatus' : 'Status'}
            value={isEPO ? values.epoStatus : values.Status}
            options={dropdownOptions.statuses}
            onChange={handleChange}
            dataTestId="status-combobox"
          />
        </div>
        <div className="load-filters-v2__cell">
          <div className={styles.dateGroup}>
            <label className="input-label label-text">
              <span>Created Date</span>
            </label>
            <div className={styles.dateRange}>
              <FilterField
                type="date"
                field="createdDateFrom"
                value={displayDate(
                  disabledFields.createdDate,
                  values.createdDateFrom,
                  defaultValues.defaultCreatedFrom
                )}
                onChange={handleDateFromChange('createdDateFrom', 'createdDateTo', defaultValues.defaultCreatedTo)}
                disabled={disabledFields.createdDate}
                hideLabel
                dataTestId="createdDateFrom-date"
              />
              <FilterField
                type="date"
                field="createdDateTo"
                value={displayDate(disabledFields.createdDate, values.createdDateTo, defaultValues.defaultCreatedTo)}
                onChange={handleDateToChange('createdDateFrom', 'createdDateTo', defaultValues.defaultCreatedFrom)}
                disabled={disabledFields.createdDate}
                hideLabel
                dataTestId="createdDateTo-date"
              />
            </div>
          </div>
        </div>

        <div className="load-filters-v2__cell">
          {showCabieExtraFields && (
            <FilterField
              label="Customer EORI"
              field="eoriNumber"
              value={values.eoriNumber}
              onChange={handleChange}
              error={getFilterMinLengthError('eoriNumber', values.eoriNumber)}
            />
          )}
        </div>
        <div className="load-filters-v2__cell">
          {showCabieExtraFields && (
            <FilterField
              label="JIRA Ticket"
              field="jiraTicket"
              value={values.jiraTicket}
              onChange={handleChange}
              allowsWildcard
            />
          )}
        </div>
        <div className="load-filters-v2__cell">
          {showEpoType && (
            <FilterField
              label="Type"
              field="Priority"
              value={values.Priority}
              options={dropdownOptions.epoTypes}
              onChange={handleChange}
              dataTestId="type-combobox"
            />
          )}
          {showCabieExtraFields && (
            <FilterField
              label="Name"
              field="Name"
              value={values.Name}
              options={dropdownOptions.agents}
              onChange={handleChange}
            />
          )}
        </div>
        <div className="load-filters-v2__cell">
          <div className={styles.checkbox}>
            <input
              type="checkbox"
              name="excludeCompleted"
              id="excludeCompleted"
              value="1"
              checked={values.excludeCompleted ?? true}
              onChange={handleCheckboxChange}
              data-testid="excludeCompleteCancel-checkbox"
            />
            <label htmlFor="excludeCompleted">Exclude Completed &amp; Cancelled</label>
          </div>
        </div>
      </div>

      <div className="load-filters-v2__actions">
        <button
          className="stepper-button yellow"
          data-testid="applyFilters-button"
          onClick={onSubmit}
          disabled={
            formHasError ||
            !hasAnyFilterSelected(values || {}, {
              excludeWhen: (k, v) => k === 'excludeCompleted' && (v === 1 || v === undefined),
            })
          }
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

LoadFiltersV2.propTypes = {
  visible: PropTypes.bool,
  values: PropTypes.shape({
    refNo: PropTypes.string,
    customerName: PropTypes.string,
    eoriNumber: PropTypes.string,
    jobReference: PropTypes.string,
    invReference: PropTypes.string,
    jiraTicket: PropTypes.string,
    cabieRoute: PropTypes.string,
    Status: PropTypes.string,
    Name: PropTypes.string,
    loadDateFrom: PropTypes.string,
    loadDateTo: PropTypes.string,
    createdDateFrom: PropTypes.string,
    createdDateTo: PropTypes.string,
    exclCompleted: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  }),
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  isEPO: PropTypes.bool,
  isEORIUser: PropTypes.bool,
  isTraderUser: PropTypes.bool,
};

export default LoadFiltersV2;
